import { isShaped, type Grid } from "../tiles";
import { KEYED, PLAIN, shapeCacheOf } from "./shapeCache";

/**
 * Smooth ground (see lifeasteroids.md, "Smooth ground"): ground's surface as
 * a curve worked out from its tiles, not a staircase. Tiles stay the data
 * (digging and building are as ever); this is the surface they make, and
 * what characters walk on, ships bump into and the client draws.
 *
 * Each tile's middle gets a fill value: a small blur of the ground round it,
 * so a staircase averages out to a slope at its true angle. Ground tiles are
 * held above the line and every other tile below it, so a dug tile is always
 * open and a single tile of ground still stands. Marching squares through
 * those values, interpolated along each edge, gives the surface as line
 * segments: gentle slopes come out gentle, curves curved.
 *
 * Ground is natural ground and ground you lay (paving, decking: anything
 * `shaped`). Other built blocks aren't ground: they stay square (or take a
 * shape of their own, see shapes.ts) and meet the curve at the tile edge.
 */

/** The line between ground and open. */
const ISO = 0.5;
/**
 * How far a tile's value is kept from the line (so it's clearly on its side),
 * the same either side of it. Flat ground and long tunnels aren't touched:
 * their values are already far from the line, and symmetric, so the surface
 * runs along the tile edges. A wall one tile thick stays a tile thick, a lone
 * tile of ground is left as a bump about half a tile across, a cliff's edge
 * is rounded a little, and a one-tile hole is a small diamond (a pocket two
 * tiles across is most of the way open; nothing fits in one tile anyway).
 */
const KEEP = 0.15;

export interface Pt {
  x: number;
  y: number;
}

/** A line segment of the surface, in grid coordinates (tile (x, y) covers [x, x+1] x [y, y+1]). Ground is on the left going from a to b. */
export interface Segment {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

/**
 * How a tile is shaped, in its own [0, 1] square: the convex `pieces` that
 * together are its solid part (each counter-clockwise), and the `surface`
 * edges along which it meets open space (ground on the left going from the
 * first point to the second), for rims and turf. See groundShape, shapes.ts.
 *
 * The smooth surface runs between tile middles, so ground reaches up to half
 * a tile into the open tiles beside it: those get pieces too (slivers of the
 * ground next door), each with an `owner`, the offset of the ground tile it
 * is drawn as. A tile's own pieces have no owner.
 */
export interface TileShape {
  pieces: Pt[][];
  surface: [Pt, Pt][];
  owners?: ([number, number] | null)[];
}

/** What counts as ground at tile (x, y) (always within the grid). */
export type Ground = (x: number, y: number) => boolean;

/** Ground as the tiles say: anything shaped (natural ground, laid ground). */
export const naturalGround =
  (g: Grid): Ground =>
  (x, y) =>
    isShaped(g.tiles[y * g.w + x]!);

/**
 * Each tile's fill value in region [x0, x1) x [y0, y1) (one tile of margin
 * read round it): above ISO for ground, below for anything else. `ground`
 * says what is ground (by default, what the tiles say); off the grid reads as
 * the nearest edge tile, so there's no surface along the grid's own edge.
 */
export function groundDensity(g: Grid, x0: number, y0: number, x1: number, y1: number, ground: Ground = naturalGround(g)): Float32Array {
  const w = x1 - x0;
  const out = new Float32Array(w * (y1 - y0));
  const at = (x: number, y: number) => (ground(Math.min(g.w - 1, Math.max(0, x)), Math.min(g.h - 1, Math.max(0, y))) ? 1 : 0);
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      // The tile counts double: the blur only shapes the edge, it doesn't decide which side a tile is on.
      const here = at(x, y);
      let sum = here * 2;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (dx || dy) sum += at(x + dx, y + dy) * (dx && dy ? 0.5 : 1);
      const blur = sum / 8;
      out[(y - y0) * w + x - x0] = here ? Math.max(ISO + KEEP, blur) : Math.min(ISO - KEEP, blur);
    }
  }
  return out;
}

/** Where along the edge from value p (at 0) to q (at 1) the line crosses. */
const cross = (p: number, q: number) => (ISO - p) / (q - p);

/**
 * The surface through one marching square: its corner values (bottom-left,
 * bottom-right, top-right, top-left) and the square's bottom-left corner at
 * (ox, oy), unit size. Each case keeps ground on the left of every segment.
 */
function cellSegments(bl: number, br: number, tr: number, tl: number, ox: number, oy: number, out: Segment[]): void {
  const code = (bl > ISO ? 1 : 0) | (br > ISO ? 2 : 0) | (tr > ISO ? 4 : 0) | (tl > ISO ? 8 : 0);
  if (code === 0 || code === 15) return;
  const bottom = { x: ox + cross(bl, br), y: oy };
  const right = { x: ox + 1, y: oy + cross(br, tr) };
  const top = { x: ox + cross(tl, tr), y: oy + 1 };
  const left = { x: ox, y: oy + cross(bl, tl) };
  const seg = (a: Pt, b: Pt) => out.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y });
  switch (code) {
    case 1: seg(bottom, left); break;
    case 2: seg(right, bottom); break;
    case 3: seg(right, left); break;
    case 4: seg(top, right); break;
    // Saddles: two corners of ground apart.
    case 5: seg(bottom, left); seg(top, right); break;
    case 6: seg(top, bottom); break;
    case 7: seg(top, left); break;
    case 8: seg(left, top); break;
    case 9: seg(bottom, top); break;
    case 10: seg(right, bottom); seg(left, top); break;
    case 11: seg(right, top); break;
    case 12: seg(left, right); break;
    case 13: seg(bottom, right); break;
    case 14: seg(left, bottom); break;
  }
}

/**
 * The surface of ground through region [x0, x1) x [y0, y1) of a grid (a
 * chunk): marching squares between the middles of its tiles. A chunk reads
 * one tile past its edge, so neighbouring chunks' surfaces meet.
 */
export function groundContour(g: Grid, x0: number, y0: number, x1: number, y1: number, ground: Ground = naturalGround(g)): Segment[] {
  // Values for the tiles of the region and one round it.
  const X0 = x0 - 1;
  const Y0 = y0 - 1;
  const w = x1 - x0 + 2;
  const h = y1 - y0 + 2;
  const d = groundDensity(g, X0, Y0, x1 + 1, y1 + 1, ground);
  const at = (i: number, j: number) => d[j * w + i]!;
  const out: Segment[] = [];
  for (let j = 0; j + 1 < h; j++) {
    for (let i = 0; i + 1 < w; i++) {
      // Tile middles are at +0.5; the square's corner (i, j) is tile (X0 + i, Y0 + j)'s middle.
      cellSegments(at(i, j), at(i + 1, j), at(i + 1, j + 1), at(i, j + 1), X0 + i + 0.5, Y0 + j + 0.5, out);
    }
  }
  // Only what lies in the region itself (the margin was for the values).
  return out.filter((s) => {
    const mx = (s.ax + s.bx) / 2;
    const my = (s.ay + s.by) / 2;
    return mx >= x0 && mx < x1 && my >= y0 && my < y1;
  });
}

const EPS = 1e-9;

/** Which side of segment `s` point `p` is on: positive to the left (the ground side). */
const sideOf = (s: Segment, p: Pt): number => (s.bx - s.ax) * (p.y - s.ay) - (s.by - s.ay) * (p.x - s.ax);

/** Does `shape` hold any ground of the tile's own (not slivers of the ground next door)? */
export const ownGround = (shape: TileShape): boolean => !shape.owners;

/** The part of convex polygon `poly` on the left of (or on) segment `s`. */
function clipLeft(poly: Pt[], s: Segment): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    const sp = sideOf(s, p);
    const sq = sideOf(s, q);
    if (sp >= -EPS) out.push(p);
    if ((sp < -EPS && sq > EPS) || (sp > EPS && sq < -EPS)) {
      const t = sp / (sp - sq);
      out.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });
    }
  }
  return out;
}

/** The part of segment `s` inside the box [x0, x1] x [y0, y1] (edges included), or null if none worth having. */
function clipToBox(s: Segment, x0: number, y0: number, x1: number, y1: number): [Pt, Pt] | null {
  let t0 = 0;
  let t1 = 1;
  const dx = s.bx - s.ax;
  const dy = s.by - s.ay;
  // Each side of the box in turn: p * t <= q.
  for (const [p, q] of [
    [-dx, s.ax - x0],
    [dx, x1 - s.ax],
    [-dy, s.ay - y0],
    [dy, y1 - s.ay],
  ] as const) {
    if (Math.abs(p) < EPS) {
      if (q < -EPS) return null;
      continue;
    }
    const t = q / p;
    if (p < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
  }
  if (t1 - t0 < 1e-6) return null;
  return [
    { x: s.ax + dx * t0, y: s.ay + dy * t0 },
    { x: s.ax + dx * t1, y: s.ay + dy * t1 },
  ];
}

/** Shapes by the ground pattern round a tile (see groundShape): the shape is a pure function of it, and there are only so many. */
const SHAPES = new Map<number, TileShape | null>();

/**
 * How the ground within tile (x, y) of `g` is shaped by the surface (see
 * groundContour) through it, in the tile's own [0, 1] square: the tile split
 * into its four quadrants, each clipped by the surface through the marching
 * square it lies in, so the pieces are convex and together are exactly the
 * ground within the tile; and the surface edges through it. For a ground
 * tile that's its solid part; for an open tile it's the slivers of the ground
 * next door that reach into it (see TileShape.owners). Null for a whole
 * square ground tile (ground all round it) and for an open tile with no
 * ground near it. `ground` says what is ground (by default, what the tiles
 * say).
 *
 * The shape depends only on the ground pattern in the 5 x 5 tiles round
 * (x, y), so it's worked out once per pattern.
 */
export function groundShape(g: Grid, x: number, y: number, ground?: Ground): TileShape | null {
  // Off the grid (a box overlapping its edge asks), the tile index would be another tile's: read it clamped, as before, and don't keep it.
  if (ground || x < 0 || y < 0 || x >= g.w || y >= g.h) {
    const key = patternKey(g, x, y, ground);
    return key < 0 ? null : shapeForKey(key);
  }
  // Read off the tiles: each tile's key is kept (see shapeCache), since collisions ask for the same tiles step after step.
  const c = shapeCacheOf(g);
  const i = y * g.w + x;
  const state = c.state[i];
  if (state === PLAIN) return null;
  if (state === KEYED) return shapeForKey(c.keys[i]!);
  const key = patternKey(g, x, y);
  if (key < 0) {
    c.state[i] = PLAIN;
    return null;
  }
  c.keys[i] = key;
  c.state[i] = KEYED;
  return shapeForKey(key);
}

/** The 5 x 5 ground pattern round (x, y) as a key (see shapeFromPattern), or -1 when there's no shape to work out. */
function patternKey(g: Grid, x: number, y: number, ground?: Ground): number {
  const { w, h, tiles } = g;
  // Clamped to the grid (off it reads as the nearest edge tile); without `ground`, what the tiles say, read straight off them.
  const at = (dx: number, dy: number) => {
    const cx = x + dx < 0 ? 0 : x + dx >= w ? w - 1 : x + dx;
    const cy = y + dy < 0 ? 0 : y + dy >= h ? h - 1 : y + dy;
    return ground ? ground(cx, cy) : isShaped(tiles[cy * w + cx]!);
  };
  const here = at(0, 0);
  // Ground all round a ground tile (a whole square), or nothing near an open one: nothing to work out. Nearly every tile, so this comes first.
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (at(dx, dy) !== here) return keyOf(at);
  return -1;
}

function keyOf(at: (dx: number, dy: number) => boolean): number {
  let key = 0;
  for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) key = key * 2 + (at(dx, dy) ? 1 : 0);
  return key;
}

function shapeForKey(key: number): TileShape | null {
  const had = SHAPES.get(key);
  if (had !== undefined) return had;
  const shape = shapeFromPattern(key);
  SHAPES.set(key, shape);
  return shape;
}

/** groundShape for the 5 x 5 ground pattern `key` (row by row from the bottom-left, most significant first) of the tile in its middle. */
function shapeFromPattern(key: number): TileShape | null {
  const bits: boolean[] = [];
  for (let k = 24; k >= 0; k--) bits.push(((key >> k) & 1) === 1);
  const pattern = (dx: number, dy: number) => bits[(dy + 2) * 5 + dx + 2]!;
  // Fill values for the 3 x 3 tiles round the middle one (each blurs its own 3 x 3).
  const value = (dx: number, dy: number) => {
    const here = pattern(dx, dy) ? 1 : 0;
    let sum = here * 2;
    for (let ey = -1; ey <= 1; ey++) for (let ex = -1; ex <= 1; ex++) if (ex || ey) sum += (pattern(dx + ex, dy + ey) ? 1 : 0) * (ex && ey ? 0.5 : 1);
    const blur = sum / 8;
    return here ? Math.max(ISO + KEEP, blur) : Math.min(ISO - KEEP, blur);
  };
  const here = pattern(0, 0);
  const pieces: Pt[][] = [];
  const surface: [Pt, Pt][] = [];
  const owners: ([number, number] | null)[] = [];
  let square = true;
  for (let qy = 0; qy < 2; qy++) {
    for (let qx = 0; qx < 2; qx++) {
      // The marching square this quadrant lies in has its corners at the middles of the tiles round the quadrant's outer corner; its origin is at (qx - 0.5, qy - 0.5) in the tile.
      const sx = qx * 2 - 1;
      const sy = qy * 2 - 1;
      const segs: Segment[] = [];
      cellSegments(value(qx - 1, qy - 1), value(qx, qy - 1), value(qx, qy), value(qx - 1, qy), qx - 0.5, qy - 0.5, segs);
      const x0 = qx * 0.5;
      const y0 = qy * 0.5;
      const quadrant: Pt[] = [
        { x: x0, y: y0 },
        { x: x0 + 0.5, y: y0 },
        { x: x0 + 0.5, y: y0 + 0.5 },
        { x: x0, y: y0 + 0.5 },
      ];
      // An open tile's slivers are drawn as the ground next door: the side neighbour toward this quadrant first, else the one above or below, else the diagonal.
      const owner: [number, number] | null = here ? null : pattern(sx, 0) ? [sx, 0] : pattern(0, sy) ? [0, sy] : [sx, sy];
      if (!segs.length) {
        // All ground (a ground tile's quadrant) or all open: a whole quadrant or none.
        if (here) {
          pieces.push(quadrant);
          owners.push(null);
        }
        continue;
      }
      square = false;
      // The ground in the cell is what's on the left of its surface: one piece of it per piece of surface (a saddle has two, at opposite corners).
      for (const s of segs) {
        const poly = clipLeft(quadrant, s);
        if (poly.length >= 3 && area(poly) > 1e-6) {
          pieces.push(poly);
          owners.push(owner);
        }
        const edge = clipToBox(s, x0, y0, x0 + 0.5, y0 + 0.5);
        if (edge) surface.push(edge);
      }
    }
  }
  if (square || !pieces.length) return null;
  // Pieces that together make one convex shape are better as one: fewer colliders and triangles. Slivers merge only with others of the same owner.
  if (here) {
    const merged = mergeConvex(pieces);
    // The surface passes close by but not through this tile (it's in the open tile next door): a whole square after all.
    if (!surface.length && merged.length === 1 && Math.abs(area(merged[0]!) - 1) < 1e-9) return null;
    return { pieces: merged, surface };
  }
  const merged: Pt[][] = [];
  const mergedOwners: ([number, number] | null)[] = [];
  const groups = new Map<string, Pt[][]>();
  pieces.forEach((piece, k) => {
    const o = owners[k]!;
    const key = `${o![0]},${o![1]}`;
    groups.set(key, [...(groups.get(key) ?? []), piece]);
  });
  for (const [key, group] of groups) {
    const [ox, oy] = key.split(",").map(Number) as [number, number];
    for (const piece of mergeConvex(group)) {
      merged.push(piece);
      mergedOwners.push([ox, oy]);
    }
  }
  return { pieces: merged, surface, owners: mergedOwners };
}

/** Convex pieces that together are convex (the hull of all their points has no more area than they do) as that one hull; otherwise as they are. */
function mergeConvex(pieces: Pt[][]): Pt[][] {
  if (pieces.length < 2) return pieces;
  const hull = convexHull(pieces.flat());
  const sum = pieces.reduce((s, p) => s + area(p), 0);
  return Math.abs(area(hull) - sum) < 1e-6 ? [hull] : pieces;
}

/** The convex hull of some points, counter-clockwise (monotone chain). */
export function convexHull(points: Pt[]): Pt[] {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y).filter((p, i, arr) => i === 0 || Math.abs(p.x - arr[i - 1]!.x) > 1e-9 || Math.abs(p.y - arr[i - 1]!.y) > 1e-9);
  if (pts.length < 3) return pts;
  const turn = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Pt[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && turn(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 1e-12) lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]!;
    while (upper.length >= 2 && turn(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 1e-12) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/** The area of a polygon (positive counter-clockwise). */
export function area(poly: Pt[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}
